import { FC, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import { Modal, LoadingDots } from '@components/ui'
import s from './WiderrufModal.module.css'

/**
 * Footer "Widerruf" (right-of-withdrawal) request.
 *
 * A non-highlighted footer button opens a small modal asking for an
 * order number + the email on the order. On submit it calls
 * `/api/widerruf`, which confirms the pair exists and opens a Re:amaze
 * conversation for support. For privacy the UI ALWAYS shows the same
 * acknowledgement regardless of whether a match was found.
 */

const EMAIL_RE = /^[\w-.+]+@([\w-]+\.)+[\w-]{2,}$/

type Status = 'idle' | 'submitting' | 'done' | 'error'

const WiderrufModal: FC<{ className?: string }> = ({ className }) => {
  const t = useTranslations('Global')
  const { locale } = useRouter()
  const [open, setOpen] = useState(false)
  const [orderId, setOrderId] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [fieldError, setFieldError] = useState(false)

  function close() {
    setOpen(false)
    // Reset after the modal is gone so the form is fresh next time.
    setTimeout(() => {
      setOrderId('')
      setEmail('')
      setStatus('idle')
      setFieldError(false)
    }, 200)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!orderId.trim() || !EMAIL_RE.test(email.trim())) {
      setFieldError(true)
      return
    }
    setFieldError(false)
    setStatus('submitting')
    try {
      const res = await fetch('/api/widerruf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: orderId.trim(),
          email: email.trim(),
          locale: locale === 'en' ? 'en' : 'de'
        })
      })
      // 200 = the request was recorded (and/or support was notified),
      // regardless of whether the order matched. Anything else means the
      // request did NOT get through — show the retry hint instead of a
      // false confirmation (a Widerruf is a statutory right).
      setStatus(res.ok ? 'done' : 'error')
    } catch (err) {
      console.error('[widerruf] submit failed', err)
      setStatus('error')
    }
  }

  return (
    <>
      <button
        type="button"
        data-testid="widerruf-trigger"
        onClick={() => setOpen(true)}
        className={className || s.triggerButton}
      >
        {t('widerruf')}
      </button>

      {open && (
        <Modal onClose={close}>
          <div className={s.body}>
            <h2 className={s.title}>{t('widerrufTitle')}</h2>

            {status === 'done' ? (
              <p className={s.feedback}>{t('widerrufFeedback')}</p>
            ) : (
              <form onSubmit={submit} noValidate>
                <p className={s.intro}>{t('widerrufIntro')}</p>

                <label className={s.label}>{t('widerrufOrderId')}</label>
                <input
                  className={s.input}
                  value={orderId}
                  onChange={(e) => {
                    setOrderId(e.target.value)
                    setFieldError(false)
                  }}
                  placeholder={t('widerrufOrderIdPlaceholder')}
                />

                <label className={s.label}>{t('widerrufEmail')}</label>
                <input
                  className={s.input}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setFieldError(false)
                  }}
                  placeholder={t('widerrufEmailPlaceholder')}
                />

                {fieldError && <p className={s.error}>{t('widerrufFieldError')}</p>}
                {status === 'error' && <p className={s.error}>{t('widerrufSubmitError')}</p>}

                <button type="submit" data-testid="widerruf-submit" className={s.submit} disabled={status === 'submitting'}>
                  {status === 'submitting' ? <LoadingDots /> : t('widerrufSubmit')}
                </button>
              </form>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}

export default WiderrufModal
